import axios from 'axios';
import { appApiUrl } from '../Utils/env';
import getXHost from '../Utils/getXHost';
import { getCsrfToken } from '../Utils/csrf';
import { useUserStore } from '../stores/user';
import { useAlerts } from '../stores/alerts';
import i18n from '../I18n';

// CWE-922: cliente central para la API. Same-origin vía el proxy /api de Netlify
// → `withCredentials` manda la cookie HttpOnly mc_token sola (no hay X-Token en
// JS). El interceptor agrega X-CSRF-Token (doble-submit) en las mutaciones.
export const apiClient = axios.create({
	baseURL: appApiUrl, // '/api' (relativo, same-origin)
	withCredentials: true,
	headers: { 'X-Host': getXHost() },
});

apiClient.interceptors.request.use((config) => {
	const method = (config.method || 'get').toUpperCase();
	const requiresCsrf =
		['POST', 'PUT', 'DELETE'].includes(method) ||
		Boolean(config.headers?.['X-CSRF-Required']);

	if (requiresCsrf) {
		const token = getCsrfToken();
		if (token && config.headers) {
			config.headers['X-CSRF-Token'] = token;
		}
	}
	// Marcador interno para GET-mutación; no debe viajar al server.
	if (config.headers) delete config.headers['X-CSRF-Required'];

	return config;
});

// Sesión expirada/inválida (401) → cerrar sesión. Antes lo hacía el interceptor
// global de axios; como apiClient es una instancia aparte hay que replicarlo, o
// el usuario queda atascado con "Expired token" en cada request sin desloguearse.
// Se saltea /logout para no entrar en loop (userLogout llama a /logout.json).
apiClient.interceptors.response.use(
	(response) => response,
	(error) => {
		const url = (error.config?.url as string | undefined) || '';
		if (error.response?.status === 401 && !url.includes('/logout')) {
			// Capturar ANTES de userLogout (que resetea isLogged). Solo se avisa
			// "sesión caducada" si REALMENTE había sesión: en la carga inicial sin
			// cookie, /me responde 401 y no hubo nada que caducar → ir al login en
			// silencio, sin alerta falsa.
			const wasLogged = useUserStore.getState().isLogged;
			useUserStore.getState().userLogout();
			if (wasLogged) {
				useAlerts.getState().addAlert({
					message: i18n.t('Current session is expired', { ns: 'Common' }),
				});
			}
		}
		return Promise.reject(error);
	}
);
